import { PostalCode } from './../model/postal-code.model';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Observable, Subject, fromEvent, map, filter, debounceTime, distinctUntilChanged } from 'rxjs';
import { MsgModel } from '../../msg/model/msg.model';
import { Country } from '../model/country.model';
import { PostalAddressService } from '../service/postal-address.service';

@Component({
    selector: 'input-zipcode-autocomplete',
    templateUrl: './input-zipcode-autocomplete.component.html',
    styleUrls: ['./input-zipcode-autocomplete.component.scss']
})
export class InputZipcodeAutocompleteComponent implements OnInit {
    @Input() id: string = 'Zipcode';
    @Input() name: string = 'Zipcode';
    @Input() label: string = 'Zipcode';
    @Input() placeholder: string = 'Zipcode';
    @Input() required: boolean = false;
    @Input() selectedZipcode: PostalCode = { POSTAL_CODE: '' };
    @Output() selectedZipcodeChange: EventEmitter<PostalCode> = new EventEmitter<PostalCode>();

    @Input() country: Country | undefined;
    @Output() countryChange: EventEmitter<Country> = new EventEmitter<Country>();

    @Input() stateId!: number;
    @Input() countryId!: number;
    @Input() disabled: boolean;
    @Input() displayRequiredMessage: boolean = false;

    @ViewChild('searchString') searchString!: NgModel;
    @ViewChild('searchStringInput', { static: true }) searchStringInput!: ElementRef;
    searchStringChanged$: Observable<any> = new Subject<any>();

    isLoading: boolean = false;
    msg: MsgModel = new MsgModel();
    requiredMsg!: MsgModel;

    minCharacters = 3;

    zipcodes!: PostalCode[];

    constructor(private postalAddressService: PostalAddressService) {}

    ngOnInit(): void {
        this.requiredMsg = new MsgModel();
        this.requiredMsg.MSG = this.name +  ' is required';
        // Search by ID if given an ID
        if (this.selectedZipcode?.ID! > 0) {
            this.isLoading = true;
            this.searchZipcodeById(this.selectedZipcode.ID).subscribe((res: any) => {
                this.isLoading = false;
                if (res['zipcode'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    this.onSelect(res['zipcode']);
                }
            });
        }
        // Search by ID if POSTAL_CODE is a number
        // else if (this.selectedZipcode.POSTAL_CODE && !isNaN(Number(this.selectedZipcode.POSTAL_CODE))) {
        //   this.isLoading = true
        //   this.searchZipcodeById(Number(this.selectedZipcode.POSTAL_CODE)).subscribe((res: any) => {
        //     this.isLoading = false;
        //     if (res['state'] == undefined) {
        //       if (res['msg'] !== undefined) {
        //         this.msg = res['msg'];
        //       }
        //     }
        //     else {
        //       this.onSelect(res['state']);
        //     }
        //   });
        // }
        // Search by POSTAL_CODE w/ no ID
        else if (
            this.selectedZipcode?.POSTAL_CODE &&
            this.selectedZipcode?.POSTAL_CODE?.length != 0 &&
            this.selectedZipcode?.POSTAL_CODE?.length > this.minCharacters
        ) {
            this.isLoading = true;
            this.searchZipcode(this.selectedZipcode.POSTAL_CODE).subscribe((res: any) => {
                this.isLoading = false;
                if (res['zipcodes'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    if (res.states?.length === 1) {
                        this.onSelect(res['zipcodes']);
                    } else {
                        let found: boolean = false;
                        for (var i = 0; i < res.states?.length; i++) {
                            if (
                                res.states[i].STATE.COUNTRy.COUNTRY_NAME === this.selectedZipcode.COUNTRy?.COUNTRY_NAME ||
                                res.states[i].STATE.COUNTRy.COUNTRY_NAME === this.country?.COUNTRY_NAME
                            ) {
                                this.onSelect(res.states[i]);
                                found = true;
                                break;
                            }
                        }

                        if (!found) {
                            for (var i = 0; i < res.states?.length; i++) {
                                if (res.states[i].CITY_NAME === this.selectedZipcode.POSTAL_CODE) {
                                    this.onSelect(res.states[i]);
                                    found = true;
                                    break;
                                }
                            }
                        }
                    }
                }
            });
        }

        fromEvent(this.searchStringInput.nativeElement, 'input')
            .pipe(
                // get value
                map((event: any) => {
                    return event.target.value;
                }),
                // only search when value has changed
                distinctUntilChanged(),
                // 500 ms delay before searching
                debounceTime(500),
                // only search if search string is >= 3 characters
                filter((res) => {
                    let canSearch = res !== null && res.length >= this.minCharacters;

                    let newZipcode = new PostalCode();
                    newZipcode.POSTAL_CODE = res;
                    this.onSelect(newZipcode);

                    return canSearch;
                })
            )
            .subscribe((text: string) => {
                this.onStartSearch(text);

                this.searchZipcode(text).subscribe((res: any) => {
                    this.isLoading = false;
                    if (res['zipcodes'] == undefined) {
                        this.msg = res['msg'];
                        let newZip = new PostalCode();
                        newZip.POSTAL_CODE = text;
                        this.onSelect(newZip);
                        this.zipcodes = [];
                    } else {
                        this.zipcodes = res['zipcodes'];

                        this.autoSelect(this.zipcodes);
                    }
                });
            });
    }

    autoSelect(zipcodes?: PostalCode[]) {
        if (!zipcodes) {
            return;
        } else if (zipcodes.length === 0) {
            return;
        } else if (zipcodes.length === 1) {
            for (var i = 0; i < zipcodes.length; i++) {
                if (zipcodes[i].POSTAL_CODE === this.selectedZipcode.POSTAL_CODE) {
                    this.onSelect(zipcodes[i]);
                    break;
                }
            }
        }
    }

    searchZipcode(searchString: any): Observable<PostalCode[]> {
        let searchCountry = new Country();
        searchCountry.ID = this.countryId;

        return this.postalAddressService.searchZipcodes(searchString, searchCountry, this.stateId);
    }

    searchZipcodeById(id: any): Observable<PostalCode> {
        return this.postalAddressService.searchZipcodesById(id);
    }

    onStartSearch(searchString: string) {
        this.isLoading = true;
        this.msg = new MsgModel();
        this.selectedZipcode = { POSTAL_CODE: searchString };
        this.checkForId(this.searchString);
    }

    onSelect(zipcode: PostalCode) {
        this.selectedZipcode = zipcode;
        this.selectedZipcodeChange.emit(this.selectedZipcode);

        if (this.selectedZipcode.COUNTRy) {
            this.country = JSON.parse(JSON.stringify(this.selectedZipcode.COUNTRy));
            this.countryChange.emit(this.country);
        }

        this.searchString.control.setValue(this.selectedZipcode.POSTAL_CODE);
        this.checkForId(this.searchString);

        this.clearAutocompleteArray();
    }

    clearAutocompleteArray() {
        this.zipcodes = [];
    }

    checkForId(input: NgModel) {
        if (this.selectedZipcode.POSTAL_CODE && this.selectedZipcode.POSTAL_CODE.length === 0) {
            input.control.setErrors({ Required: 'Please search and select a valid ' + this.name });
        }
        if (!this.selectedZipcode.ID) {
            input.control.setErrors({ idRequired: 'Please search and select a valid ' + this.name });
        } else {
            input.control.setErrors(null);
        }
    }
}
